import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import {
  collection, onSnapshot, doc, updateDoc, query, orderBy, Timestamp,addDoc
} from "firebase/firestore";
import styled, { keyframes, css, createGlobalStyle } from "styled-components";
import { enqueueSnackbar } from "notistack";
import { auth } from "../firebaseConfig";

// ─── Constants ────────────────────────────────────────────────────────────────

const BANKS_GBP = [
  "NATWEST", "BARCLAYS", "LLOYDS", "HSBC", "SANTANDER",
  "HALIFAX", "MONZO", "STARLING", "TSB", "METRO BANK",
];

const BANKS_PKR = [
  "ALLIED", "HBL", "MCB", "UBL", "MEEZAN", "FAYSAL",
  "BANK ALFALAH", "ASKARI", "SILK BANK", "SUMMIT BANK",
];

const REFUND_REASONS = [
  "TICKET VOIDED", "FLIGHT CANCELLED", "HOTEL CANCELLED", "VISA REJECTED",
  "CUSTOMER REQUEST", "DUPLICATE BOOKING", "DATE CHANGE", "PARTIAL REFUND", "NIL", "OTHER",
];

const getDisplayName = (email) => {
  if (!email) return "User";
  return email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideRight = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.55; transform: scale(0.85); }
`;

// ─── Global ───────────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #f1f5f9;
  font-family: 'DM Sans', sans-serif;
  padding: 36px 24px 80px;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 28px;
  animation: ${fadeUp} 0.4s ease both;
`;

const HeaderLeft = styled.div``;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;

  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #f43f5e;
    animation: ${pulse} 2s ease infinite;
  }
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.4px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
`;

// ─── Stats ────────────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 22px;
  animation: ${fadeUp} 0.4s ease 0.05s both;

  @media (min-width: 640px) { grid-template-columns: repeat(4, 1fr); }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $color }) => $color};
    border-radius: 12px 12px 0 0;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 6px;
  }

  .stat-value {
    font-size: 22px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.5px;
    line-height: 1;
    font-family: 'DM Mono', monospace;
  }

  .stat-sub {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 4px;
  }

  .stat-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 22px;
    opacity: 0.15;
  }
`;

// ─── Filter Bar ───────────────────────────────────────────────────────────────

const FilterBar = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  animation: ${fadeUp} 0.4s ease 0.1s both;
`;

const FilterLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #94a3b8;
  white-space: nowrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  transition: all 0.15s;
  border: 1.5px solid ${({ $active, $color }) => $active ? $color : "#e2e8f0"};
  background: ${({ $active, $bg }) => $active ? $bg : "transparent"};
  color: ${({ $active, $color }) => $active ? $color : "#64748b"};

  &:hover {
    border-color: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 180px;
  padding: 7px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  transition: all 0.15s;

  &:focus {
    border-color: #f43f5e;
    background: white;
    box-shadow: 0 0 0 3px #f43f5e18;
  }
  &::placeholder { color: #cbd5e1; }
`;

// ─── Request Grid ─────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 14px;
  animation: ${fadeUp} 0.4s ease 0.15s both;
`;

const RequestCard = styled.div`
  background: white;
  border-radius: 14px;
  border: 1.5px solid ${({ $currency }) => $currency === "PKR" ? "#bbf7d0" : "#e2e8f0"};
  padding: 18px;
  cursor: pointer;
  transition: all 0.18s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${({ $i }) => Math.min($i * 40, 300)}ms;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $currency }) => $currency === "PKR" ? "#059669" : "#2563eb"};
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px ${({ $currency }) => $currency === "PKR" ? "#05996922" : "#2563eb22"};
    border-color: ${({ $currency }) => $currency === "PKR" ? "#059669" : "#2563eb"}66;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CurrencyTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $c }) => $c === "PKR" ? "#ecfdf5" : "#eff6ff"};
  color: ${({ $c }) => $c === "PKR" ? "#059669" : "#2563eb"};
`;

const CardField = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 5px;
  font-size: 13px;

  .lbl {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    min-width: 70px;
    flex-shrink: 0;
  }

  .val {
    color: #334155;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mono {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: #475569;
  }
`;

const AmountBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;

  .input-amt {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    font-weight: 600;
    color: ${({ $c }) => $c === "PKR" ? "#059669" : "#2563eb"};
  }

  .gbp-amt {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: #64748b;
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 64px 24px;
  color: #94a3b8;

  .icon { font-size: 40px; margin-bottom: 12px; opacity: 0.4; }
  .msg  { font-size: 15px; font-weight: 500; color: #64748b; }
  .sub  { font-size: 13px; margin-top: 4px; }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease both;
`;

const Modal = styled.div`
  background: white;
  border-radius: 18px;
  width: min(560px, 100%);
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(0,0,0,0.22);
  animation: ${slideRight} 0.25s ease both;
`;

const ModalHeader = styled.div`
  padding: 22px 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .icon-wrap {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: ${({ $currency }) => $currency === "PKR" ? "#ecfdf5" : "#eff6ff"};
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
  }

  span {
    font-size: 12px;
    color: #94a3b8;
  }
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: all 0.12s;
  &:hover { background: #fee2e2; color: #ef4444; border-color: #fca5a5; }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
`;

const FieldGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
`;

const ModalInput = styled.input`
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  width: 100%;
  transition: all 0.15s;

  &:focus {
    border-color: ${({ $color }) => $color || "#f43f5e"};
    background: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#f43f5e"}18;
  }
  &::placeholder { color: #cbd5e1; }
  &:read-only { opacity: 0.65; background: #f1f5f9; cursor: not-allowed; }
`;

const ModalSelect = styled.select`
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center;
  color: #0f172a;
  width: 100%;
  appearance: none;
  padding-right: 30px;
  cursor: pointer;
  transition: all 0.15s;

  &:focus {
    border-color: ${({ $color }) => $color || "#f43f5e"};
    background-color: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#f43f5e"}18;
  }
`;

// ─── Conversion Info in Modal ─────────────────────────────────────────────────

const ConversionRow = styled.div`
  background: linear-gradient(135deg, #ecfdf5, #f0fdf4);
  border: 1.5px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  .label { font-size: 12px; color: #065f46; font-weight: 500; }
  .value {
    font-family: 'DM Mono', monospace;
    font-size: 14px;
    font-weight: 600;
    color: #059669;
  }
`;

const GBPRow = styled.div`
  background: #eff6ff;
  border: 1.5px solid #bfdbfe;
  border-radius: 10px;
  padding: 12px 16px;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .label { font-size: 12px; color: #1e40af; font-weight: 500; }
  .value {
    font-family: 'DM Mono', monospace;
    font-size: 15px;
    font-weight: 600;
    color: #2563eb;
  }
`;

// ─── Modal Footer ─────────────────────────────────────────────────────────────

const ModalFooter = styled.div`
  padding: 16px 24px 22px;
  display: flex;
  gap: 10px;
  border-top: 1px solid #f1f5f9;
`;

const ProcessBtn = styled.button`
  flex: 1;
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white;
  background: #059669;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover { background: #047857; transform: translateY(-1px); box-shadow: 0 6px 20px #05996944; }
  &:active { transform: none; }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const CancelBtn = styled.button`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #64748b;
  background: #f1f5f9;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #e2e8f0; color: #0f172a; }
`;

// ─── Section Divider ──────────────────────────────────────────────────────────

const SectionDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 0 24px 16px;
`;

const SectionTag = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #94a3b8;
  padding: 0 24px 10px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function RefundProcessingDashboard() {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterCurrency, setFilterCurrency] = useState("All");
  const [filterStatus, setFilterStatus] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    setUser(auth.currentUser);
  }, []);

  useEffect(() => {
    const q = query(collection(db, "customerRefundRequests"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const handleChange = (field, value) =>
    setSelectedRequest({ ...selectedRequest, [field]: value });

  const calcGBP = (req) => {
    if (req.currency === "GBP") return parseFloat(req.amountInput || 0).toFixed(2);
    const amt = parseFloat(req.amountInput || 0);
    const rate = parseFloat(req.rate || 0);
    if (!amt || !rate) return "0.00";
    return (amt / rate).toFixed(2);
  };

  const handleMarkRefunded = async () => {
    if (!selectedRequest) return;
    setSaving(true);
    try {
      const gbp = parseFloat(calcGBP(selectedRequest));
      await updateDoc(doc(db, "customerRefundRequests", selectedRequest.id), {
        status: "REFUNDED",
        refundedBy: getDisplayName(user?.email),
        amountGBP: gbp,
        amountInput: selectedRequest.amountInput,
        bank: selectedRequest.bank,
        beneficiary: selectedRequest.beneficiary,
        rate: selectedRequest.rate || "",
        date: selectedRequest.date,
        reason: selectedRequest.reason,
        remarks: selectedRequest.remarks || "",
        folderNo: selectedRequest.folderNo,
      });

      await addDoc(collection(db, "customerRefunds"), {
        refundedBy: getDisplayName(user?.email),
        amountGBP: gbp,
        amountInput: selectedRequest.amountInput,
        bank: selectedRequest.bank,
        beneficiary: selectedRequest.beneficiary,
        rate: selectedRequest.rate || "",
        date: selectedRequest.date,
        reason: selectedRequest.reason,
        remarks: selectedRequest.remarks || "",
        folderNo: selectedRequest.folderNo,
        timestamp: Timestamp.now(),
      });
    
      enqueueSnackbar("Refund marked as processed ✓", { variant: "success" });
      setSelectedRequest(null);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error updating refund", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ── Filtering ───────────────────────────────────────────────────────────────

  const filtered = requests.filter((r) => {
    const matchCurrency = filterCurrency === "All" || r.currency === filterCurrency;
    const matchStatus = filterStatus === "All" || r.status === filterStatus;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (r.folderNo || "").toLowerCase().includes(q) ||
      (r.beneficiary || "").toLowerCase().includes(q) ||
      (r.agent || "").toLowerCase().includes(q) ||
      (r.bank || "").toLowerCase().includes(q) ||
      (r.reason || "").toLowerCase().includes(q);
    return matchCurrency && matchStatus && matchSearch;
  });

  // ── Stats ───────────────────────────────────────────────────────────────────

  const pending = requests.filter((r) => r.status === "PENDING");
  const refunded = requests.filter((r) => r.status === "REFUNDED");
  const totalGBP = requests.reduce((s, r) => s + (parseFloat(r.amountGBP) || 0), 0);
  const pendingGBP = pending.reduce((s, r) => s + (parseFloat(r.amountGBP) || 0), 0);

  const accentColor = selectedRequest?.currency === "PKR" ? "#059669" : "#2563eb";
  const bankList = selectedRequest?.currency === "PKR" ? BANKS_PKR : BANKS_GBP;

  return (
    <>
      <GlobalStyle />
      <Page>
        <Inner>

          {/* ── Header ── */}
          <Header>
            <HeaderLeft>
              <Badge>Live Queue</Badge>
              <PageTitle>Refund Processing</PageTitle>
              <PageSub>Review, edit and process pending customer refund requests</PageSub>
            </HeaderLeft>
          </Header>

          {/* ── Stats ── */}
          <StatsRow>
            <StatCard $color="#f59e0b">
              <div className="stat-label">Pending</div>
              <div className="stat-value">{pending.length}</div>
              <div className="stat-sub">awaiting action</div>
              <div className="stat-icon">⏳</div>
            </StatCard>

            <StatCard $color="#10b981">
              <div className="stat-label">Processed</div>
              <div className="stat-value">{refunded.length}</div>
              <div className="stat-sub">refunded</div>
              <div className="stat-icon">✅</div>
            </StatCard>

            <StatCard $color="#f43f5e">
              <div className="stat-label">Pending £</div>
              <div className="stat-value">£{pendingGBP.toFixed(0)}</div>
              <div className="stat-sub">outstanding</div>
              <div className="stat-icon">⚠️</div>
            </StatCard>

            <StatCard $color="#2563eb">
              <div className="stat-label">Total £</div>
              <div className="stat-value">£{totalGBP.toFixed(0)}</div>
              <div className="stat-sub">all refunds</div>
              <div className="stat-icon">💷</div>
            </StatCard>
          </StatsRow>

          {/* ── Filter Bar ── */}
          <FilterBar>
            <FilterLabel>Currency</FilterLabel>
            <FilterGroup>
              {[
                { key: "All", color: "#64748b", bg: "#f1f5f9", label: "All" },
                { key: "GBP", color: "#2563eb", bg: "#eff6ff", label: "🇬🇧 GBP" },
                { key: "PKR", color: "#059669", bg: "#ecfdf5", label: "🇵🇰 PKR" },
              ].map(({ key, color, bg, label }) => (
                <FilterChip
                  key={key}
                  $active={filterCurrency === key}
                  $color={color}
                  $bg={bg}
                  onClick={() => setFilterCurrency(key)}
                >
                  {label}
                </FilterChip>
              ))}
            </FilterGroup>

            <FilterLabel>Status</FilterLabel>
            <FilterGroup style={{ flex: "none" }}>
              {[
                { key: "All", color: "#64748b", bg: "#f1f5f9" },
                { key: "PENDING", color: "#92400e", bg: "#fffbeb" },
                { key: "REFUNDED", color: "#065f46", bg: "#ecfdf5" },
              ].map(({ key, color, bg }) => (
                <FilterChip
                  key={key}
                  $active={filterStatus === key}
                  $color={color}
                  $bg={bg}
                  onClick={() => setFilterStatus(key)}
                >
                  {key}
                </FilterChip>
              ))}
            </FilterGroup>

            <SearchInput
              placeholder="Search folder, beneficiary, agent, bank…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterBar>

          {/* ── Cards Grid ── */}
          <Grid>
            {filtered.length === 0 && (
              <EmptyState>
                <div className="icon">📭</div>
                <div className="msg">No refund requests found</div>
                <div className="sub">
                  {search || filterCurrency !== "All" || filterStatus !== "All"
                    ? "Try adjusting your filters"
                    : "All caught up — nothing pending"}
                </div>
              </EmptyState>
            )}

            {filtered.map((req, i) => (
              <RequestCard
                key={req.id}
                $currency={req.currency}
                $i={i}
                onClick={() => setSelectedRequest({ ...req })}
              >
                <CardTop>
                  <CurrencyTag $c={req.currency}>
                    {req.currency === "GBP" ? "🇬🇧" : "🇵🇰"} {req.currency}
                  </CurrencyTag>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    fontSize: 11, fontWeight: 600,
                    color: req.status === "PENDING" ? "#92400e" : "#065f46",
                    background: req.status === "PENDING" ? "#fffbeb" : "#ecfdf5",
                    padding: "3px 9px", borderRadius: 20
                  }}>
                    <span style={{
                      width: 5, height: 5, borderRadius: "50%",
                      background: req.status === "PENDING" ? "#f59e0b" : "#10b981",
                      display: "inline-block"
                    }} />
                    {req.status}
                  </span>
                </CardTop>

                <CardField>
                  <span className="lbl">Folder</span>
                  <span className="mono">#{req.folderNo}</span>
                </CardField>
                <CardField>
                  <span className="lbl">Beneficiary</span>
                  <span className="val">{req.beneficiary}</span>
                </CardField>
                <CardField>
                  <span className="lbl">Agent</span>
                  <span className="val">{req.agent}</span>
                </CardField>
                <CardField>
                  <span className="lbl">Bank</span>
                  <span className="val">{req.bank}</span>
                </CardField>
                <CardField>
                  <span className="lbl">Reason</span>
                  <span className="val">{req.reason}</span>
                </CardField>

                <AmountBadge $c={req.currency}>
                  <span className="input-amt">
                    {req.currency === "GBP" ? "£" : "₨"}
                    {parseFloat(req.amountInput || 0).toLocaleString()}
                  </span>
                  {req.currency === "PKR" && (
                    <span className="gbp-amt">
                      → £{parseFloat(req.amountGBP || 0).toFixed(2)}
                    </span>
                  )}
                  {req.currency === "GBP" && (
                    <span className="gbp-amt">GBP</span>
                  )}
                </AmountBadge>
              </RequestCard>
            ))}
          </Grid>

          {/* ── Modal ── */}
          {selectedRequest && (
            <Overlay onClick={() => setSelectedRequest(null)}>
              <Modal onClick={(e) => e.stopPropagation()}>

                <ModalHeader>
                  <ModalTitleWrap $currency={selectedRequest.currency}>
                    <div className="icon-wrap">
                      {selectedRequest.currency === "GBP" ? "🇬🇧" : "🇵🇰"}
                    </div>
                    <div>
                      <h3>Process Refund</h3>
                      <span>
                        {selectedRequest.currency} · Folder #{selectedRequest.folderNo} · {selectedRequest.agent}
                      </span>
                    </div>
                  </ModalTitleWrap>
                  <CloseBtn onClick={() => setSelectedRequest(null)}>×</CloseBtn>
                </ModalHeader>

                <ModalBody>
                  {/* Amount summary */}
                  {selectedRequest.currency === "PKR" ? (
                    <ConversionRow>
                      <span className="label">
                        ₨{parseFloat(selectedRequest.amountInput || 0).toLocaleString()}
                        {selectedRequest.rate && ` ÷ ${selectedRequest.rate}`}
                      </span>
                      <span className="value">= £{calcGBP(selectedRequest)} GBP</span>
                    </ConversionRow>
                  ) : (
                    <GBPRow>
                      <span className="label">Refund Amount</span>
                      <span className="value">£{calcGBP(selectedRequest)}</span>
                    </GBPRow>
                  )}

                  <FieldGrid2>
                    <Field>
                      <label>Folder No</label>
                      <ModalInput
                        value={selectedRequest.folderNo}
                        onChange={(e) => handleChange("folderNo", e.target.value)}
                        $color={accentColor}
                      />
                    </Field>

                    <Field>
                      <label>Beneficiary</label>
                      <ModalInput
                        value={selectedRequest.beneficiary}
                        onChange={(e) => handleChange("beneficiary", e.target.value.toUpperCase())}
                        $color={accentColor}
                      />
                    </Field>

                    <Field>
                      <label>Bank</label>
                      <ModalSelect
                        value={selectedRequest.bank}
                        onChange={(e) => handleChange("bank", e.target.value)}
                        $color={accentColor}
                      >
                        <option value="">Select bank</option>
                        {bankList.map((b) => <option key={b}>{b}</option>)}
                      </ModalSelect>
                    </Field>

                    <Field>
                      <label>
                        Amount ({selectedRequest.currency === "GBP" ? "£" : "₨"})
                      </label>
                      <ModalInput
                        type="number"
                        value={selectedRequest.amountInput}
                        onChange={(e) => handleChange("amountInput", e.target.value)}
                        $color={accentColor}
                      />
                    </Field>

                    {selectedRequest.currency === "PKR" && (
                      <Field>
                        <label>Rate (PKR → £)</label>
                        <ModalInput
                          type="number"
                          placeholder="e.g. 380"
                          value={selectedRequest.rate || ""}
                          onChange={(e) => handleChange("rate", e.target.value)}
                          $color={accentColor}
                        />
                      </Field>
                    )}

                    <Field>
                      <label>Date</label>
                      <ModalInput
                        type="date"
                        value={selectedRequest.date || ""}
                        onChange={(e) => handleChange("date", e.target.value)}
                        $color={accentColor}
                      />
                    </Field>

                    <Field>
                      <label>Reason</label>
                      <ModalSelect
                        value={selectedRequest.reason}
                        onChange={(e) => handleChange("reason", e.target.value)}
                        $color={accentColor}
                      >
                        {REFUND_REASONS.map((r) => <option key={r}>{r}</option>)}
                      </ModalSelect>
                    </Field>

                    <Field>
                      <label>Agent</label>
                      <ModalInput value={selectedRequest.agent} readOnly $color={accentColor} />
                    </Field>

                    <Field>
                      <label>Refunded By</label>
                      <ModalInput
                        value={selectedRequest.refundedBy || getDisplayName(user?.email)}
                        onChange={(e) => handleChange("refundedBy", e.target.value)}
                        $color={accentColor}
                      />
                    </Field>

                    <Field style={{ gridColumn: "1 / -1" }}>
                      <label>Remarks</label>
                      <textarea
                        value={selectedRequest.remarks || ""}
                        onChange={(e) => handleChange("remarks", e.target.value)}
                        placeholder="Any notes…"
                        style={{
                          width: "100%",
                          minHeight: 80,
                          padding: "9px 12px",
                          fontSize: 13,
                          fontFamily: "'DM Sans', sans-serif",
                          border: "1.5px solid #e2e8f0",
                          borderRadius: 8,
                          outline: "none",
                          background: "#f8fafc",
                          color: "#0f172a",
                          resize: "vertical",
                          transition: "all 0.15s",
                        }}
                      />
                    </Field>
                  </FieldGrid2>
                </ModalBody>

                <ModalFooter>
                  <ProcessBtn onClick={handleMarkRefunded} disabled={saving || selectedRequest.status === "REFUNDED"}>
                    {saving ? "Saving…" : selectedRequest.status === "REFUNDED" ? "✓ Already Refunded" : "✓ Mark as Refunded"}
                  </ProcessBtn>
                  <CancelBtn onClick={() => setSelectedRequest(null)}>Cancel</CancelBtn>
                </ModalFooter>

              </Modal>
            </Overlay>
          )}

        </Inner>
      </Page>
    </>
  );
}